import { pxToEm } from 'util/pxToEm';
import React, { ReactElement } from 'react';
import { Box, Typography } from '@mui/material';
import { LeaderRequest } from 'services/api/apis/organizations/organizations.api';
import { LeaderRequestPendingState } from '../../LeaderRequestPendingState';
import { LeaderInvitePendingState } from '../../LeaderInvitePendingState';

// Helper to safely get role from leader request
const getLeaderRequestRole = (request: LeaderRequest | null | undefined): string => {
  if (!request) return '';
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
  return typeof (request ).role === 'string' ? (request ).role : '';
};

// Helper to safely get inviteSource from leader request
const getLeaderRequestInviteSource = (request: LeaderRequest | null | undefined): 'admin' | 'user' => {
  if (!request) return 'user';
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const source = (request ).inviteSource;
  return source === 'admin' ? 'admin' : 'user';
};

export interface OrganizationLeader {
  id: string;
  firstName: string;
  lastName: string;
  orgRole: string;
}

export interface OrganizationLeadersSectionProps {
  orgLeaders: OrganizationLeader[];
  /** @deprecated Section is always expanded per the CAM-669 redesign. Retained for backward compatibility with callers. */
  isExpanded?: boolean;
  /** @deprecated Section is always expanded per the CAM-669 redesign. Retained for backward compatibility with callers. */
  onToggle?: () => void;
  isEligibleForLeaderRequest: boolean;
  hasPendingRequest: boolean;
  myLeaderRequest: LeaderRequest | null | undefined;
  organizationName?: string;
  schoolTermsUrl?: string;
  schoolTermsDisplayName?: string;
  onOpenRoleModal: () => void;
  onEditRequest: () => void;
  onCancelRequest: () => Promise<void>;
  onAcceptInvite: (acceptedTerms: boolean) => Promise<void>;
  onDeclineInvite: () => Promise<void>;
  /** True when the current user is an accepted leader of this organization (CAM-684). */
  isAcceptedLeader?: boolean;
  onLeaveMyRole?: () => Promise<void>;
}

const LeaderItem = ({ leader }: { leader: OrganizationLeader }): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <Typography sx={{ fontFamily: 'Poppins', fontSize: pxToEm(14), fontWeight: 400, color: '#383838' }}>
      {leader.firstName} {leader.lastName}
    </Typography>
    <Typography sx={{ fontFamily: 'Poppins', fontSize: pxToEm(14), fontWeight: 400, color: '#383838' }}>
      {leader.orgRole}
    </Typography>
  </Box>
);

const InlineActionButton = ({
  label,
  onClick,
  variant = 'primary',
}: {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'danger';
}): ReactElement => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 47,
      width: 320,
      maxWidth: 320,
      borderRadius: '6px',
      border: `1px solid ${variant === 'danger' ? '#9B0000' : '#23305F'}`,
      cursor: 'pointer',
      backgroundColor: '#FFFFFF',
      '&:hover': { backgroundColor: '#F8F8F8' },
    }}
  >
    <Typography
      sx={{
        fontFamily: 'Poppins',
        fontSize: 14,
        fontWeight: 400,
        color: variant === 'danger' ? '#9B0000' : '#23305F',
        textAlign: 'center',
        lineHeight: 'normal',
      }}
    >
      {label}
    </Typography>
  </Box>
);

export const OrganizationLeadersSection = ({
  orgLeaders,
  isEligibleForLeaderRequest,
  hasPendingRequest,
  myLeaderRequest,
  organizationName,
  schoolTermsUrl,
  schoolTermsDisplayName,
  onOpenRoleModal,
  onEditRequest,
  onCancelRequest,
  onAcceptInvite,
  onDeclineInvite,
  isAcceptedLeader = false,
  onLeaveMyRole,
}: OrganizationLeadersSectionProps): ReactElement => {
  const inviteSource = getLeaderRequestInviteSource(myLeaderRequest);
  const isAdminInvite = inviteSource === 'admin';
  const hasLeaders = orgLeaders.length > 0;

  return (
    <Box border="1px solid #F0F0F0" borderRadius="4px" overflow="hidden">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#F0F0F0',
          height: 37,
          px: 16,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Poppins',
            fontWeight: 500,
            fontSize: pxToEm(14),
            color: '#383838',
          }}
        >
          Organization Leaders
        </Typography>
      </Box>
      <Box p={16} gap={8} display="flex" flexDirection="column">
        {hasLeaders ? (
          orgLeaders.map(leader => <LeaderItem key={leader.id} leader={leader} />)
        ) : (
          <Box>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 600,
                fontSize: pxToEm(14),
                color: '#383838',
              }}
            >
              No leaders listed yet
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 400,
                fontSize: pxToEm(14),
                color: '#6B6B6B',
                mt: 2,
              }}
            >
              Be the first to get added as a leader.
            </Typography>
          </Box>
        )}

        {hasPendingRequest && myLeaderRequest && (
          <Box mt={hasLeaders ? 16 : 0}>
            {isAdminInvite ? (
              <LeaderInvitePendingState
                requestedRole={getLeaderRequestRole(myLeaderRequest)}
                organizationName={organizationName}
                schoolTermsUrl={schoolTermsUrl}
                schoolTermsDisplayName={schoolTermsDisplayName}
                onAccept={onAcceptInvite}
                onDecline={onDeclineInvite}
              />
            ) : (
              <LeaderRequestPendingState
                requestedRole={getLeaderRequestRole(myLeaderRequest)}
                onEdit={onEditRequest}
                onCancel={onCancelRequest}
              />
            )}
          </Box>
        )}

        {isAcceptedLeader && onLeaveMyRole ? (
          <Box mt={16} display="flex" justifyContent="center">
            <InlineActionButton
              variant="danger"
              label="Leave My Role"
              onClick={() => {
                void onLeaveMyRole();
              }}
            />
          </Box>
        ) : isEligibleForLeaderRequest && !hasPendingRequest ? (
          <Box mt={16} display="flex" justifyContent="center">
            <InlineActionButton
              label="Request access"
              onClick={onOpenRoleModal}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
